import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, FileText, BookOpen, CheckSquare, Globe, MapPin, Building2 } from 'lucide-react';
import { HelpDialog } from '@/components/HelpDialog';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    competitions: 0,
    pages: 0,
    content: 0,
    pendingApprovals: 0
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpTopicKey, setHelpTopicKey] = useState(null);

  // The six Getting Started tiles. Keys match the backend seeded topics.
  const gettingStartedItems = [
    { key: 'venues-clubs',     title: 'Set up your venues and clubs',    tagline: 'Manage participating organisations' },
    { key: 'event-schedule',   title: 'Tailor your event schedule',      tagline: 'Create a schedule for your special events' },
    { key: 'first-competition',title: 'Create your first competition',   tagline: 'Add competition details and dates' },
    { key: 'publish-pages',    title: 'Build and publish pages',         tagline: 'Add competition details and dates' },
    { key: 'event-documents',  title: 'Create and send event documents', tagline: 'Manage your communication & admin' },
    { key: 'entries-oos',      title: 'Manage entries & order of events',tagline: 'Skaters in, events follow!' },
  ];
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user first - this is critical
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        
        // Fetch other data with individual error handling. The Content
        // Library has no aggregate endpoint, so sum the five sub-collections.
        const contentBuckets = [
          'disciplines', 'schedules', 'grades', 'figures', 'compulsory-dances'
        ];
        const fetchPromises = [
          axios.get(`${API}/competitions`, { withCredentials: true }),
          axios.get(`${API}/competition-pages`, { withCredentials: true }),
          ...contentBuckets.map(b =>
            axios.get(`${API}/content/${b}`, { withCredentials: true })
          ),
        ];
        const approvalsIdx = fetchPromises.length;
        if (userRes.data?.role === 'super') {
          fetchPromises.push(axios.get(`${API}/approvals`, { withCredentials: true }));
        }

        const results = await Promise.allSettled(fetchPromises);
        const compsRes = results[0];
        const pagesRes = results[1];
        const contentResults = results.slice(2, 2 + contentBuckets.length);
        const approvalsRes = results[approvalsIdx];

        let pendingCount = 0;
        if (approvalsRes?.status === 'fulfilled') {
          pendingCount = approvalsRes.value.data.filter(a => a.status === 'pending').length;
        }

        const contentCount = contentResults.reduce(
          (sum, r) => sum + (r.status === 'fulfilled' && Array.isArray(r.value.data) ? r.value.data.length : 0),
          0
        );

        setStats({
          competitions: compsRes.status === 'fulfilled' ? compsRes.value.data.length : 0,
          pages: pagesRes.status === 'fulfilled' ? pagesRes.value.data.length : 0,
          content: contentCount,
          pendingApprovals: pendingCount
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  // Determine competition label based on user role
  const competitionsLabel = loading ? 'Competitions' : (user?.role === 'super' ? 'Competitions' : 'Your Competitions');
  
  return (
    <div className="space-y-8 fade-in">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
          Welcome back{user ? `, ${user.name}` : ''}!
        </h1>
        <p className="text-lg text-muted-foreground">Here's what's happening with your competitions</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="p-6 bg-stone-50 rounded-xl border border-stone-200 cursor-pointer hover:shadow-md hover:border-stone-300 transition-all"
          onClick={() => navigate('/competitions')}
          data-testid="dashboard-tile-competitions"
        >
          <div className="flex items-center justify-between mb-4">
            <Trophy className="h-8 w-8" style={{ color: '#2D4A3E' }} />
            <span className="text-3xl font-bold" style={{ color: '#2D4A3E' }}>{stats.competitions}</span>
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {competitionsLabel}
          </p>
        </Card>
        
        <Card 
          className="p-6 bg-stone-50 rounded-xl border border-stone-200 cursor-pointer hover:shadow-md hover:border-stone-300 transition-all"
          onClick={() => navigate('/competition-pages')}
          data-testid="dashboard-tile-pages"
        >
          <div className="flex items-center justify-between mb-4">
            <Globe className="h-8 w-8" style={{ color: '#D4A373' }} />
            <span className="text-3xl font-bold" style={{ color: '#D4A373' }}>{stats.pages}</span>
          </div>
          <p className="text-sm font-medium text-muted-foreground">Competition Pages</p>
        </Card>
        
        <Card 
          className="p-6 bg-stone-50 rounded-xl border border-stone-200 cursor-pointer hover:shadow-md hover:border-stone-300 transition-all"
          onClick={() => navigate('/content')}
          data-testid="dashboard-tile-content"
        >
          <div className="flex items-center justify-between mb-4">
            <BookOpen className="h-8 w-8" style={{ color: '#E9C46A' }} />
            <span className="text-3xl font-bold" style={{ color: '#E9C46A' }}>{stats.content}</span>
          </div>
          <p className="text-sm font-medium text-muted-foreground">Content Items</p>
        </Card>
        
        {user?.role === 'super' && (
          <Card 
            className="p-6 bg-stone-50 rounded-xl border border-stone-200 cursor-pointer hover:shadow-md hover:border-stone-300 transition-all"
            onClick={() => navigate('/admin/approvals')}
            data-testid="dashboard-tile-approvals"
          >
            <div className="flex items-center justify-between mb-4">
              <CheckSquare className="h-8 w-8" style={{ color: '#2A9D8F' }} />
              <span className="text-3xl font-bold" style={{ color: '#2A9D8F' }}>{stats.pendingApprovals}</span>
            </div>
            <p className="text-sm font-medium text-muted-foreground">Pending Approvals</p>
          </Card>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-8 card-hover">
          <h3 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Getting Started</h3>
          <p className="text-sm text-muted-foreground mb-4">A simple guide to starting your first CompCreator event</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col gap-x-6 gap-y-4">
            {gettingStartedItems.map((item, idx) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setHelpTopicKey(item.key)}
                className="flex items-start gap-3 text-left rounded-lg px-2 py-1.5 -mx-2 hover:bg-stone-50 transition-colors"
                data-testid={`getting-started-${item.key}`}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: '#2D4A3E' }}
                >
                  <span className="text-white text-xs font-bold">{idx + 1}</span>
                </div>
                <div>
                  <p className="font-medium leading-tight">{item.title}</p>
                  <p className="text-sm text-muted-foreground">{item.tagline}</p>
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-8 card-hover">
          <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Quick Actions</h3>
          <div className="space-y-5">
            <Link to="/content?action=create-schedule">
              <Button variant="outline" className="w-full justify-start hover:bg-[#E9C46A] hover:text-[#2D4A3E] hover:border-[#E9C46A] transition-colors" data-testid="quick-create-schedule">
                <BookOpen className="mr-2 h-5 w-5" style={{ color: '#E9C46A' }} />
                Create Custom Event Schedule
              </Button>
            </Link>
            <Link to="/admin/people-places?tab=venues&action=create">
              <Button variant="outline" className="w-full justify-start hover:bg-[#E9C46A] hover:text-[#2D4A3E] hover:border-[#E9C46A] transition-colors" data-testid="quick-create-venue">
                <MapPin className="mr-2 h-5 w-5" style={{ color: '#2A9D8F' }} />
                Set up a skating venue
              </Button>
            </Link>
            <Link to="/admin/people-places?tab=orgs&action=create">
              <Button variant="outline" className="w-full justify-start hover:bg-[#E9C46A] hover:text-[#2D4A3E] hover:border-[#E9C46A] transition-colors" data-testid="quick-create-organisation">
                <Building2 className="mr-2 h-5 w-5" style={{ color: '#7C5A44' }} />
                Add a new skating organisation - clubs, committees, national body.
              </Button>
            </Link>
            <Link to="/competitions?action=create">
              <Button variant="outline" className="w-full justify-start hover:bg-[#E9C46A] hover:text-[#2D4A3E] hover:border-[#E9C46A] transition-colors" data-testid="quick-create-competition">
                <Trophy className="mr-2 h-5 w-5" style={{ color: '#2D4A3E' }} />
                Create New Competition
              </Button>
            </Link>
            <Link to="/competition-pages/create">
              <Button variant="outline" className="w-full justify-start hover:bg-[#E9C46A] hover:text-[#2D4A3E] hover:border-[#E9C46A] transition-colors" data-testid="quick-create-page">
                <Globe className="mr-2 h-5 w-5" style={{ color: '#D4A373' }} />
                Create Competition Page
              </Button>
            </Link>
            <Link to="/documents">
              <Button variant="outline" className="w-full justify-start hover:bg-[#E9C46A] hover:text-[#2D4A3E] hover:border-[#E9C46A] transition-colors" data-testid="quick-generate-document">
                <FileText className="mr-2 h-5 w-5" />
                Generate Documents
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      <HelpDialog
        open={!!helpTopicKey}
        onOpenChange={(v) => { if (!v) setHelpTopicKey(null); }}
        topicKey={helpTopicKey}
      />
    </div>
  );
}
